// KI-Fach- & Akte-Assistent (DSGVO-Klasse Grün) — Chat-Oberfläche.

import React, { useEffect, useRef, useState } from 'react';
import { useMandantStore } from '../../store/mandantStore';
import {
  chatAssistant, summarizeAkte, isAssistantAvailable, type ChatMessage,
} from '../../services/aiAssistantService';

const BEISPIELE = [
  'Welches Untersuchungsintervall gilt bei Lärmexposition über 85 dB(A)?',
  'Wer muss verpflichtend an einer ASA-Sitzung teilnehmen?',
  'Wie oft ist eine Betriebsbegehung nach §77 ASchG durchzuführen?',
  'Was gehört in ein VGÜ-Untersuchungsprogramm bei Schweißern?',
];

export function AssistentModul(): React.JSX.Element {
  const mandant = useMandantStore(s => s.getAktiverMandant());
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput]       = useState('');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const verfuegbar = isAssistantAvailable();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const senden = async (text: string): Promise<void> => {
    const frage = text.trim();
    if (frage === '' || busy) return;
    setError('');
    const neu: ChatMessage[] = [...messages, { role: 'user', content: frage }];
    setMessages(neu);
    setInput('');
    setBusy(true);
    try {
      const antwort = await chatAssistant(neu);
      setMessages([...neu, { role: 'assistant', content: antwort }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setMessages(neu);
    } finally {
      setBusy(false);
    }
  };

  const akteZusammenfassen = async (): Promise<void> => {
    if (mandant === null || mandant === undefined || busy) return;
    setError('');
    const frage: ChatMessage = { role: 'user', content: `Fasse die Firmen-Akte von ${mandant.name} zusammen.` };
    setMessages(m => [...m, frage]);
    setBusy(true);
    try {
      const antwort = await summarizeAkte(mandant.id, mandant.name);
      setMessages(m => [...m, { role: 'assistant', content: antwort }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-4">
        <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          🤖 KI-Assistent
        </h1>
        <p className="text-xs text-gray-400">
          Fach- & Rechtsfragen der Arbeitsmedizin · Akte zusammenfassen
          {mandant !== null && mandant !== undefined ? ` · ${mandant.name}` : ''}
        </p>
      </div>

      {!verfuegbar ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="card max-w-md text-center space-y-2">
            <p className="text-3xl">🔑</p>
            <p className="font-medium text-gray-700 dark:text-gray-300">Kein KI-Anbieter eingerichtet</p>
            <p className="text-sm text-gray-500">
              Der Assistent läuft mit jedem gängigen KI-Anbieter. Unter
              Einstellungen → Sicherheit → <span className="font-medium">KI-Assistent</span> einen
              Anbieter wählen (z.B. OpenRouter, OpenAI, Anthropic, Gemini …) und dessen API-Key
              hinterlegen. Empfohlen ist jeweils ein gutes, günstiges Modell (⭐).
              Der Key wird nur lokal gespeichert und nicht synchronisiert.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Nachrichten */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 max-w-3xl mx-auto w-full">
            {messages.length === 0 && (
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  Stellen Sie eine arbeitsmedizinische Fach- oder Rechtsfrage — oder lassen Sie die
                  Firmen-Akte zusammenfassen. Beispiele:
                </p>
                <div className="grid gap-2">
                  {BEISPIELE.map(b => (
                    <button key={b} onClick={() => { void senden(b); }}
                      className="text-left text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700
                                 text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600 transition-colors">
                      {b}
                    </button>
                  ))}
                </div>
                {mandant !== null && mandant !== undefined && (
                  <button onClick={() => { void akteZusammenfassen(); }}
                    className="btn-secondary text-sm">
                    🗂 Akte „{mandant.name}" zusammenfassen
                  </button>
                )}
                <p className="text-xs text-amber-600 dark:text-amber-500">
                  ⚠ Nur administrative/fachliche Fragen — keine identifizierenden Patientendaten oder Befunde eingeben.
                </p>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl px-4 py-3 flex items-center gap-2 text-sm text-gray-500">
                  <span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  denkt nach…
                </div>
              </div>
            )}

            {error !== '' && (
              <p className="text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg p-3">✕ {error}</p>
            )}
          </div>

          {/* Eingabe */}
          <div className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
            <div className="max-w-3xl mx-auto flex items-end gap-2">
              {mandant !== null && mandant !== undefined && messages.length > 0 && (
                <button onClick={() => { void akteZusammenfassen(); }} disabled={busy}
                  title="Firmen-Akte zusammenfassen"
                  className="btn-secondary text-sm flex-shrink-0 disabled:opacity-50">🗂</button>
              )}
              <textarea value={input}
                onChange={e => { setInput(e.target.value); }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void senden(input); } }}
                placeholder="Fachfrage stellen… (Enter zum Senden)"
                rows={1}
                className="input-field flex-1 resize-none max-h-32"
                autoCapitalize="sentences" />
              <button onClick={() => { void senden(input); }} disabled={busy || input.trim() === ''}
                className="btn-primary flex-shrink-0 disabled:opacity-50">Senden</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
